/**
 * @class
 * @function FacetsToggle
 */
if (!customElements.get('facets-toggle')) {
  class FacetsToggle extends HTMLElement {
    connectedCallback() {
      this.drawer = document.getElementById('Facet-Drawer');

      this.addEventListener('click', (e) => {
        e.preventDefault();

        if (OverlayManager.isOpen(this.drawer)) {
          OverlayManager.close(this.drawer);
        } else {
          OverlayManager.open(this.drawer);
        }

        setTimeout(() => {
          window.dispatchEvent(new Event('resize.resize-select'));
        }, 10);
      });
    }
  }

  customElements.define('facets-toggle', FacetsToggle);
}


/**
 * COPA:
 * Return the number of actual product cards currently displayed.
 *
 * Using .product-card instead of .column prevents promotion blocks
 * and other collection-grid elements from being counted as products.
 */
function getDisplayedProductCount() {
  return document.querySelectorAll('#product-grid .product-card').length;
}


/**
 * COPA:
 * Localized result label.
 */
function getDisplayedProductCountLabel() {
  const translations = {
    en: 'results',
    de: 'Ergebnisse',
    fr: 'résultats',
    es: 'resultados',
    it: 'risultati',
    nl: 'resultaten',
    'pt-PT': 'resultados'
  };

  const currentLang =
    window.Shopify && window.Shopify.locale
      ? window.Shopify.locale
      : 'en';

  return translations[currentLang] || translations.en;
}


/**
 * COPA:
 * Update every visible product/result counter with the amount of
 * products actually rendered in the collection/search grid.
 */
function updateDisplayedProductCount() {
  const productGrid = document.getElementById('product-grid');

  if (!productGrid) return;

  const displayedProducts = getDisplayedProductCount();
  const translatedText = getDisplayedProductCountLabel();

  const countElements = document.querySelectorAll(
    '.thb-filter-count, #ProductCount .facets__label'
  );

  countElements.forEach((element) => {
    element.innerText = `${displayedProducts} ${translatedText}`;
    element.classList.remove('loading');
  });
}


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener(
      'input',
      this.debouncedOnSubmit.bind(this)
    );
  }


  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }


  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }


  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');

    const productGridContainer =
      document.getElementById('ProductGridContainer');

    const collection = productGridContainer?.querySelector('.collection');

    if (collection) {
      collection.classList.add('loading');
    }

    for (const item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url =
        `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }

    FacetFiltersForm.publishFacetUpdate(searchParams);
  }


  /**
   * Shopify Standard Events
   *
   * Keeps Shopify collection/search analytics intact when
   * filtering or sorting.
   */
  static publishFacetUpdate(searchParams) {
    const SE = window.Shopify && window.Shopify.StandardEvents;

    if (!SE) return;

    const params = new URLSearchParams(searchParams);

    if (theme.settings.collection_id) {
      publishStandardEvent('CollectionUpdateEvent', {
        collection: {
          id: theme.settings.collection_id
        },
        filters: SE.CollectionUpdateEvent.parseProductFilters(params),
        sortKey: SE.CollectionUpdateEvent.getSortKey(params)
      });
    } else if (
      window.location.pathname.indexOf(theme.routes.search_url) !== -1
    ) {
      publishStandardEvent('SearchUpdateEvent', {
        searchTerm:
          params.get('q') ||
          new URLSearchParams(window.location.search).get('q') ||
          '',
        filters: SE.SearchUpdateEvent.parseProductFilters(params),
        sortKey: SE.SearchUpdateEvent.getSortKey(params)
      });
    }
  }


  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url
          }
        ];

        withViewTransition(() => {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
        });
      });
  }


  static renderSectionFromCache(filterDataUrl, event) {
    const html =
      FacetFiltersForm.filterData.find(filterDataUrl).html;

    withViewTransition(() => {
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
    });
  }


  static renderProductGridContainer(html) {
    const currentContainer =
      document.getElementById('ProductGridContainer');

    const newContainer = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer');

    if (!currentContainer || !newContainer) return;

    currentContainer.innerHTML = newContainer.innerHTML;

    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height'),
        10
      ) || 0;

    const top =
      currentContainer.getBoundingClientRect().top +
      document.documentElement.scrollTop -
      headerHeight -
      30;

    window.scrollTo({
      top,
      left: 0,
      behavior: 'smooth'
    });
  }


  /**
   * COPA CUSTOMIZATION
   *
   * Shopify normally supplies ProductCount from the section HTML.
   * COPA intentionally uses the amount of product cards that are
   * actually rendered instead.
   */
  static renderProductCount() {
    updateDisplayedProductCount();
  }


  static renderFilters(html, event) {
    const parsedHTML =
      new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm collapsible-row, ' +
        '#FacetFiltersFormMobile collapsible-row, ' +
        '#FacetFiltersForm-bar collapsible-row'
      );

    const matchesIndex = (element) => {
      const jsFilter = event
        ? event.target.closest('collapsible-row')
        : undefined;

      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };

    const facetsToRender =
      Array.from(facetDetailsElements)
        .filter((element) => !matchesIndex(element));

    const countsToRender =
      Array.from(facetDetailsElements)
        .find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `collapsible-row[data-index="${element.dataset.index}"]`
      );

      if (target) {
        target.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event) {
      const target = event.target.closest('collapsible-row');

      if (target) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          target
        );
      }
    }
  }


  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets'
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement =
        html.querySelector(selector);

      if (!activeFacetsElement) return;

      const target =
        document.querySelector(selector);

      if (target) {
        target.innerHTML =
          activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }


  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-filter-count'
    ];

    mobileElementSelectors.forEach((selector) => {
      const source =
        html.querySelector(selector);

      const target =
        document.querySelector(selector);

      if (!source || !target) return;

      target.innerHTML = source.innerHTML;
    });
  }


  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetElement =
      target.querySelector('.facets__selected');

    const sourceElement =
      source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      targetElement.outerHTML =
        sourceElement.outerHTML;
    }
  }


  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams
      },
      '',
      `${window.location.pathname}${
        searchParams
          ? '?'.concat(searchParams)
          : ''
      }`
    );
  }


  static getSections() {
    const productGrid =
      document.getElementById('product-grid');

    if (!productGrid) return [];

    return [
      {
        section: productGrid.dataset.id
      }
    ];
  }


  onSubmitHandler(event) {
    event.preventDefault();

    const form =
      event.target.closest('form');

    if (!form) return;

    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    if (
      searchParams.get('filter.v.price.gte') === '0.00'
    ) {
      searchParams.delete('filter.v.price.gte');
    }

    const priceSlider =
      document.querySelector('.price_slider');

    if (priceSlider) {
      const maxPrice =
        parseFloat(priceSlider.dataset.max)
          .toFixed(2);

      if (
        searchParams.get('filter.v.price.lte') === maxPrice
      ) {
        searchParams.delete('filter.v.price.lte');
      }
    }

    FacetFiltersForm.renderPage(
      searchParams.toString(),
      event
    );
  }


  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const href = event.currentTarget.href;

    const url =
      href.indexOf('?') === -1
        ? ''
        : href.slice(href.indexOf('?') + 1);

    FacetFiltersForm.renderPage(url);
  }
}


FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define(
    'facet-filters-form',
    FacetFiltersForm
  );
}

FacetFiltersForm.setListeners();


class FacetRemove extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();

        const form =
          this.closest('facet-filters-form') ||
          document.querySelector('facet-filters-form');

        if (form) {
          form.onActiveFilterClick(event);
        }
      });
    });
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define(
    'facet-remove',
    FacetRemove
  );
}


/**
 * @class
 * @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const slider =
      this.querySelector('.price_slider');

    const amounts =
      this.querySelector('.price_slider_amount');

    if (!slider || !amounts) return;

    const args = {
      start: [
        parseFloat(
          slider.dataset.minValue || 0
        ),
        parseFloat(
          slider.dataset.maxValue ||
          slider.dataset.max
        )
      ],

      connect: true,
      step: 10,
      direction: document.dir,

      handleAttributes: [
        {
          'aria-label': 'lower'
        },
        {
          'aria-label': 'upper'
        }
      ],

      range: {
        min: 0,
        max: parseFloat(slider.dataset.max)
      }
    };

    const event =
      new CustomEvent('input');

    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }

    noUiSlider.create(slider, args);

    slider.noUiSlider.on(
      'update',
      function (values) {
        const minimum =
          amounts.querySelector('.field__input_min');

        const maximum =
          amounts.querySelector('.field__input_max');

        if (minimum) {
          minimum.value = values[0];
        }

        if (maximum) {
          maximum.value = values[1];
        }
      }
    );

    slider.noUiSlider.on(
      'change',
      function () {
        if (form) {
          form
            .querySelector('form')
            ?.dispatchEvent(event);
        }
      }
    );
  }
}

if (!customElements.get('price-slider')) {
  customElements.define(
    'price-slider',
    PriceSlider
  );
}


/**
 * Theme 17:
 * Prevent colour-swatch filter tooltips from overflowing
 * the viewport.
 */
(function () {
  const updateShift = (label) => {
    const content =
      label.querySelector('.facet-tooltip__content');

    if (!content) return;

    label.style.setProperty(
      '--tooltip-shift',
      '0px'
    );

    const rect =
      content.getBoundingClientRect();

    const gutter = 8;
    const vw =
      document.documentElement.clientWidth;

    let shift = 0;

    if (rect.left < gutter) {
      shift =
        Math.round(
          gutter - rect.left
        );
    } else if (rect.right > vw - gutter) {
      shift =
        Math.round(
          vw - gutter - rect.right
        );
    }

    label.style.setProperty(
      '--tooltip-shift',
      `${shift}px`
    );
  };


  document.addEventListener(
    'mouseover',
    (event) => {
      if (!event.target.closest) return;

      const label =
        event.target.closest(
          '.facet-checkbox--tooltip'
        );

      if (
        !label ||
        label._tooltipHovered
      ) {
        return;
      }

      label._tooltipHovered = true;

      updateShift(label);
    }
  );


  document.addEventListener(
    'mouseout',
    (event) => {
      if (!event.target.closest) return;

      const label =
        event.target.closest(
          '.facet-checkbox--tooltip'
        );

      if (!label) return;

      if (
        !label.contains(
          event.relatedTarget
        )
      ) {
        label._tooltipHovered = false;
      }
    }
  );
})();


/**
 * COPA:
 * Initial displayed-product result count.
 */
document.addEventListener(
  'DOMContentLoaded',
  () => {
    updateDisplayedProductCount();

    /**
     * Some COPA collection/search logic can modify the
     * contents of ProductGridContainer after the original
     * Shopify section render.
     *
     * Watch those changes and recalculate the real visible
     * product count.
     */
    const targetNode =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!targetNode) return;

    let updateTimeout;

    const observer =
      new MutationObserver(
        (mutationsList) => {
          const shouldUpdate =
            mutationsList.some(
              (mutation) =>
                mutation.addedNodes.length ||
                mutation.removedNodes.length
            );

          if (!shouldUpdate) return;

          clearTimeout(updateTimeout);

          updateTimeout =
            setTimeout(() => {
              updateDisplayedProductCount();
            }, 300);
        }
      );

    observer.observe(
      targetNode,
      {
        childList: true,
        subtree: true
      }
    );
  }
);